/* eslint-disable react-refresh/only-export-components */
import React, { FunctionComponent, lazy, LazyExoticComponent } from 'react'

import { BOOK_TYPE } from 'app/constants/ebook.constants'
import { HUB_PROGRAMS } from 'app/constants/entitlement.constant'
import {
  allowedBibliotecaAndSpanishReaderGrades,
  allowedSoundsAndSonidosGrades,
  allowedTheaterGrades,
} from 'app/constants/home-content-buttons.constants'
import routesConstants from 'app/constants/routes.constants'
import { SOUNDS_TYPE } from 'app/constants/sounds.constants'
import EntitlementService from 'app/services/core/entitlement.service'
import { RootState } from 'app/store'

const Home = lazy(() => import('app/pages/home/home.page'))
const Theater = lazy(() => import('app/pages/theater/theater.page'))
const Library = lazy(() => import('app/pages/library/library.page'))
const Sounds = lazy(() => import('app/pages/sounds/sounds.component'))
const TeacherGuide = lazy(
  () => import('app/pages/teacher-guide/teacher-guide.page')
)
const AvatarBuilderPage = lazy(
  () => import('app/pages/avatar-builder/avatar-builder.page')
)
const ReaderContainer = lazy(
  () => import('app/pages/e-reader/e-reader.container')
)
const ServerErrorPage = lazy(() => import('app/pages/errors/serverError.page'))
const NotFoundPage = lazy(() => import('app/pages/errors/notFound.page'))
const ConnectionLostPage = lazy(
  () => import('app/pages/errors/connectionLost.page')
)
const NoLicensePage = lazy(() => import('app/pages/errors/noLicense.page'))

export type ROOT_ROUTE = {
  path: string
  component: FunctionComponent | LazyExoticComponent<any>
  exact?: boolean
  checkAccess?: (appState: RootState) => boolean
}

const rootRoutes: ROOT_ROUTE[] = [
  {
    exact: true,
    path: routesConstants.LIBRARY,
    component: Library,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
    }),
  },
  {
    exact: true,
    path: routesConstants.LIBRARY3,
    component: () => <Library bookCategory={BOOK_TYPE.LIBRARY3} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA3],
    }),
  },
  {
    exact: true,
    path: routesConstants.BIBLIOTECA,
    component: () => <Library bookCategory={BOOK_TYPE.BIBLIOTECA} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
      allowedGrades: allowedBibliotecaAndSpanishReaderGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS,
    component: () => <Library bookCategory={BOOK_TYPE.CAMINOS_BIBLIOTECA} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS],
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS_3,
    component: () => <Library bookCategory={BOOK_TYPE.CAMINOS_3_BIBLIOTECA} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS_3],
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS_3_CA,
    component: () => (
      <Library bookCategory={BOOK_TYPE.CAMINOS_3_CA_BIBLIOTECA} />
    ),
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS_3_CA],
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_ELAR,
    component: () => <Library bookCategory={BOOK_TYPE.TEXAS_ELAR} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.TEXAS_ELAR,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_SLAR,
    component: () => <Library bookCategory={BOOK_TYPE.TEXAS_SLAR} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.TEXAS_SLAR,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_ELAR_2E,
    component: () => <Library bookCategory={BOOK_TYPE.TEXAS_ELAR_2E} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_ELAR_2E],
      checkAllPrograms: false,
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_SLAR_2E,
    component: () => <Library bookCategory={BOOK_TYPE.TEXAS_SLAR_2E} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_SLAR_2E],
      checkAllPrograms: false,
    }),
  },
  {
    exact: true,
    path: routesConstants.SOUNDS,
    component: Sounds,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.CKLA,
        HUB_PROGRAMS.CKLA3,
        HUB_PROGRAMS.TEXAS_ELAR,
        HUB_PROGRAMS.TEXAS_ELAR_2E,
        HUB_PROGRAMS.TEXAS_IMRA,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
      allowedGrades: allowedSoundsAndSonidosGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.SOUNDS_ID,
    component: Sounds,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.CKLA,
        HUB_PROGRAMS.TEXAS_ELAR,
        HUB_PROGRAMS.TEXAS_ELAR_2E,
        HUB_PROGRAMS.TEXAS_IMRA,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
      allowedGrades: allowedSoundsAndSonidosGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.SONIDOS,
    component: () => <Sounds soundsCategory={SOUNDS_TYPE.SONIDOS} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.CAMINOS,
        HUB_PROGRAMS.CAMINOS_3,
        HUB_PROGRAMS.CAMINOS_3_CA,
        HUB_PROGRAMS.TEXAS_SLAR,
        HUB_PROGRAMS.TEXAS_SLAR_2E,
        HUB_PROGRAMS.TEXAS_IMRA,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
      allowedGrades: allowedSoundsAndSonidosGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.SONIDOS_ID,
    component: () => <Sounds soundsCategory={SOUNDS_TYPE.SONIDOS} />,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [
        HUB_PROGRAMS.CAMINOS,
        HUB_PROGRAMS.CAMINOS_3,
        HUB_PROGRAMS.CAMINOS_3_CA,
        HUB_PROGRAMS.TEXAS_SLAR,
        HUB_PROGRAMS.TEXAS_SLAR_2E,
        HUB_PROGRAMS.TEXAS_IMRA,
        HUB_PROGRAMS.TEXAS_IMRA_COMMERCIAL,
      ],
      checkAllPrograms: false,
      allowedGrades: allowedSoundsAndSonidosGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.THEATER,
    component: Theater,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
      allowedGrades: allowedTheaterGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.THEATER_ID,
    component: Theater,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
      allowedGrades: allowedTheaterGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
    }),
  },
  {
    exact: true,
    path: routesConstants.LIBRARY_3_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA3],
    }),
  },
  {
    exact: true,
    path: routesConstants.SPANISH_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CKLA],
      allowedGrades: allowedBibliotecaAndSpanishReaderGrades,
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS],
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS_3_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS_3],
    }),
  },
  {
    exact: true,
    path: routesConstants.CAMINOS_3_CA_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.CAMINOS_3_CA],
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_ELAR_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_ELAR],
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_SLAR_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_SLAR],
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_ELAR_2E_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_ELAR_2E],
    }),
  },
  {
    exact: true,
    path: routesConstants.TEXAS_SLAR_2E_READER_ID,
    component: ReaderContainer,
    checkAccess: EntitlementService.getPageAccessChecker({
      allowedPrograms: [HUB_PROGRAMS.TEXAS_SLAR_2E],
    }),
  },
  {
    exact: false,
    path: routesConstants.TEACHER_GUIDE,
    component: TeacherGuide,
    checkAccess: EntitlementService.getTeacherGuideAccessCheck(),
  },
  {
    exact: true,
    path: routesConstants.AVATAR_BUILDER,
    component: AvatarBuilderPage,
  },
  {
    exact: true,
    path: '*/403',
    component: NoLicensePage,
  },
  {
    exact: true,
    path: '*/408',
    component: ConnectionLostPage,
  },
  {
    exact: true,
    path: '*/500',
    component: ServerErrorPage,
  },
  {
    exact: true,
    path: '/50x.html',
    component: ConnectionLostPage,
  },
  {
    exact: true,
    path: routesConstants.HOME,
    component: Home,
  },
  {
    path: '*',
    component: NotFoundPage,
  },
]

export default rootRoutes
